import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
} from "react";
import { hrLogin, hrLogout, hrAuthStatus } from "../api/hrUserApi";
import { notifications } from "@mantine/notifications";
import { getDepartmentSelectData } from "../api/departmentApi";
import { getTagSelectData, TagKind } from "../api/tagApi";
import { getContractSelectData } from "../api/contractApi";
import { getTerminalSelectData } from "../api/terminalApi";
import { errorNotification } from "../utils/useNotification";

const AuthContext = createContext<{
  user: any;
  company: any;
  isAuthenticated: any;
  employmentTypes: any;
  departmentSelectData: any;
  /** Etiketler türe göre gruplu: { role: [], skill: [], custom: [] } */
  tagSelectData: Record<TagKind, any[]>;
  /** Geriye dönük kolaylık — tagSelectData.role / .skill kısayolları */
  roleSelectData: any;
  skillSelectData: any;
  contractSelectData: any;
  terminalSelectData: any;
  login: (phone: string, password: string, companyCode: string) => void;
  logout: () => void;
  setCompany: React.Dispatch<React.SetStateAction<any>>;
  refetchDepartmentSelectData: () => void;
  refetchTagSelectData: () => void;
  refetchContractSelectData: () => void;
  refetchTerminalSelectData: () => void;
}>(null!);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<any>(null);
  const [company, setCompany] = useState<any>(null);
  const [token, setToken] = useState<string>("");
  const hasFetched = useRef(false); // İlk isteği kontrol etmek için
  // Select-data istekleri paralel atıldığı için backend düştüğünde 4 ayrı
  // bildirim yağardı; bir tur boyunca tek uyarı gösteriyoruz.
  const selectDataErrorShown = useRef(false);

  //select data
  const [employmentTypes, setEmploymentTypes] = useState();
  const [departmentSelectData, setDepartmentSelectData] = useState();
  const [tagSelectData, setTagSelectData] = useState<Record<TagKind, any[]>>({
    role: [],
    skill: [],
  });
  const [contractSelectData, setContractSelectData] = useState();
  const [terminalSelectData, setTerminalSelectData] = useState();

  // Daha önce bu catch'ler tamamen sessizdi: backend düştüğünde dropdown'lar
  // sebepsiz boş geliyor, kullanıcı "departman yok" sanıyordu.
  const notifySelectDataFailure = (what: string, err: unknown) => {
    console.error(`${what} seçenekleri alınamadı:`, err);
    if (selectDataErrorShown.current) return;
    selectDataErrorShown.current = true;
    errorNotification({
      title: "Veriler Yüklenemedi",
      message:
        "Bazı seçenek listeleri sunucudan alınamadı. Sayfayı yenilemeyi deneyin.",
    });
  };

  const fetchTerminalSelectData = async () => {
    try {
      const { data: response }: any = await getTerminalSelectData();
      setTerminalSelectData(response?.data);
    } catch (err) {
      notifySelectDataFailure("Terminal", err);
    }
  };

  const fetchDepartmentSelectData = async () => {
    try {
      const { data: response }: any = await getDepartmentSelectData();
      setDepartmentSelectData(response?.data);
    } catch (err) {
      notifySelectDataFailure("Departman", err);
    }
  };

  // Tek istekle tüm etiketler çekilip türe göre gruplanır (eski hâlde rol ve
  // yetenek için iki ayrı istek atılıyordu).
  const fetchTagSelectData = async () => {
    try {
      const { data: response }: any = await getTagSelectData();
      const grouped: Record<TagKind, any[]> = { role: [], skill: [] };
      for (const tag of response?.data ?? []) {
        // Tanınmayan tür (ör. göç öncesi kalmış `custom`) yeteneğe düşer.
        const kind: TagKind = grouped[tag.kind as TagKind] ? tag.kind : "skill";
        grouped[kind].push(tag);
      }
      setTagSelectData(grouped);
    } catch (err) {
      notifySelectDataFailure("Rol / Yetenek", err);
    }
  };

  const fetchContractSelectdata = async () => {
    try {
      const { data: response }: any = await getContractSelectData();
      setContractSelectData(response?.data);
    } catch (err) {
      notifySelectDataFailure("Kontrat", err);
    }
  };

  const authStatus = async (token: string) => {
    const res: any = await hrAuthStatus(token);
    if (res?.data?.loggedIn) {
      setToken(res?.data?.token);
      localStorage.setItem("token", res?.data?.token);
      setIsAuthenticated(true);
      setCompany(res?.data?.company);
      setEmploymentTypes(res?.data?.company?.employmentTypes);
      setUser(res?.data?.user);
      selectDataErrorShown.current = false;
      fetchDepartmentSelectData();
      fetchTagSelectData();
      fetchContractSelectdata();
      fetchTerminalSelectData();
    } else {
      setIsAuthenticated(false);
      setUser(null);
      setCompany(null);
      setToken("");
      localStorage.removeItem("token");
    }
    return res;
  };

  const login = async (
    phone: string,
    password: string,
    companyCode: string
  ) => {
    const res: any = await hrLogin(phone, password, companyCode);
    if (res?.data?.success && res?.data?.token) {
      setUser(res?.data?.user);
      setCompany(res?.data?.company);
      setToken(res?.data?.token);
      localStorage.setItem("token", res?.data?.token);
      setIsAuthenticated(true);
      selectDataErrorShown.current = false;
      fetchDepartmentSelectData();
      fetchTagSelectData();
      fetchContractSelectdata();
      fetchTerminalSelectData();
      notifications.show({
        title: `Giriş Başarılı ${res?.data?.user.name} !`,
        message: `Hoş Geldiniz.`,
        autoClose: 4000,
        withBorder: true,
        color: "green",
      });
    } else {
      notifications.show({
        title: "Hata!",
        message: `${res?.response?.data?.message ?? 'Sunucuya bağlanılamadı'}`,
        autoClose: 4000,
        withBorder: true,
        color: "red",
      });
    }
  };

  const logout = async () => {
    const res: any = await hrLogout(token);
    if (res.data.loggedout) {
      notifications.show({
        title: "Çıkış Başarılı " + user?.name + " !",
        message: `İyi Günler Dileriz.`,
        autoClose: 4000,
        withBorder: true,
        color: "green",
      });
      setUser(null);
      setCompany(null);
      setToken("");
      setIsAuthenticated(false);
      localStorage.removeItem("token");
    }
  };

  useEffect(() => {
    if (hasFetched.current) return; // İkinci çalışmayı engelle
    hasFetched.current = true;
    const storedToken = localStorage.getItem("token");

    if (storedToken) {
      authStatus(storedToken);
    } else {
      setIsAuthenticated(false);
    }
  }, []);

  useEffect(() => {
    if (company) {
      localStorage.setItem("company", JSON.stringify(company));
    } else {
      localStorage.removeItem("company");
    }
  }, [company]);

  return (
    <AuthContext.Provider
      value={{
        user,
        company,
        employmentTypes,
        isAuthenticated,
        departmentSelectData,
        tagSelectData,
        roleSelectData: tagSelectData.role,
        skillSelectData: tagSelectData.skill,
        contractSelectData,
        terminalSelectData,
        login,
        logout,
        setCompany,
        refetchDepartmentSelectData: fetchDepartmentSelectData,
        refetchTagSelectData: fetchTagSelectData,
        refetchContractSelectData: fetchContractSelectdata,
        refetchTerminalSelectData: fetchTerminalSelectData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
